import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScene } from "../scene/SceneContext";
import "./AiChatDrawer.css";

function getChatBaseUrl() {
  const configured = String(process.env.REACT_APP_MCP_HTTP_URL || "").replace(/\/+$/, "");
  if (configured) return configured;
  if (process.env.NODE_ENV === "development") return "http://localhost:3002";
  return "";
}

async function streamChatMessage(params) {
  const { sessionId, message, onEvent, context, signal } = params;
  const base = getChatBaseUrl();
  const url = `${base}/chat/stream`;

  const resp = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ sessionId, message, context }),
    signal,
  });

  if (!resp.ok || !resp.body) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `HTTP ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const frame of parts) {
        const lines = frame.split("\n");
        const dataLines = lines
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice("data:".length).trim());
        if (!dataLines.length) continue;
        const data = dataLines.join("\n");
        try {
          const obj = JSON.parse(data);
          if (typeof onEvent === "function") onEvent(obj);
        } catch {
          // ignore
        }
      }
    }
  } catch (e) {
    // If the caller aborted after receiving "final", treat as normal completion.
    if (signal && signal.aborted) return;
    throw e;
  }
}

export default function AiChatDrawer({ groupId, groupName } = {}) {
  const { t } = useTranslation();
  const { variant } = useScene();

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [streamStatus, setStreamStatus] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState(() => {
    const base = t("chat.welcome");
    const ctx =
      groupName && String(groupName).trim()
        ? t("chat.group_context_named", { groupName })
        : groupId
          ? t("chat.group_context_id", { groupId })
          : null;
    return [{ role: "assistant", content: ctx ? `${base}\n\n${ctx}` : base }];
  });

  const listRef = useRef(null);

  const canSend = useMemo(() => input.trim() && !isSending, [input, isSending]);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    setError(null);
    setIsSending(true);
    setInput("");

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setTimeout(scrollToBottom, 0);

    try {
      const abort = new AbortController();
      let finalContent = null;
      let finalized = false;
      await streamChatMessage({
        sessionId,
        message: text,
        context: { groupId, groupName },
        signal: abort.signal,
        onEvent: (evt) => {
          if (!evt || typeof evt !== "object") return;
          if (evt.type === "meta") {
            if (evt.sessionId && !sessionId) setSessionId(evt.sessionId);
          }
          if (evt.type === "status" && typeof evt.message === "string") setStreamStatus(evt.message);
          if (evt.type === "tool_call" && evt.name) setStreamStatus(t("chat.status_calling", { tool: evt.name }));
          if (evt.type === "tool_result" && evt.name) setStreamStatus(t("chat.status_finished", { tool: evt.name }));
          if (evt.type === "error" && evt.detail) setError(String(evt.detail));
          if (evt.type === "final" && typeof evt.content === "string") {
            finalContent = evt.content;
            finalized = true;
            // Render immediately on "final" instead of waiting for the stream to close.
            const assistantText = String(evt.content || "").trim();
            setMessages((prev) => [...prev, { role: "assistant", content: assistantText || "(empty response)" }]);
            setIsSending(false);
            setStreamStatus(null);
            setTimeout(scrollToBottom, 0);
            // Close the SSE request promptly.
            abort.abort();
          }
        },
      });

      // Fallback (should be rare): if stream ended without a final event.
      if (!finalized) {
        setStreamStatus(null);
        const assistantText = String(finalContent || "").trim();
        setMessages((prev) => [...prev, { role: "assistant", content: assistantText || "(empty response)" }]);
        setTimeout(scrollToBottom, 0);
      }
    } catch (e) {
      setError(String(e?.message || e));
      setInput(text);
    } finally {
      setIsSending(false);
      setStreamStatus(null);
    }
  };

  return (
    <>
      <button
        type="button"
        className="ai-chat-fab as-btn-primary-gradient"
        onClick={() => setOpen(true)}
        title={t("general.ai_chat")}
        aria-label={t("general.ai_chat")}
      >
        <span className="ai-chat-fab__emoji" aria-hidden="true">
          🐾
        </span>
        <span className="ai-chat-fab__label">{t("general.ai_chat")}</span>
      </button>

      {open ? (
        <>
          <div className="ai-chat-backdrop" onClick={() => setOpen(false)} />
          <div className={`ai-chat-drawer ${variant === "night" ? "ai-chat-drawer--night" : ""}`}>
            <div className="ai-chat-drawer__header">
              <div className="ai-chat-drawer__title">
                <span aria-hidden="true">🦊</span>
                <span>{t("general.ai_chat")}</span>
              </div>
              <div className="ai-chat-drawer__actions">
                <button type="button" className="btn btn-sm as-btn-soft" onClick={() => setOpen(false)} title={t("general.close")}>
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>

            <div ref={listRef} className="ai-chat-drawer__body">
              {messages.map((m, idx) => (
                <div key={idx} className={`ai-chat-msg ${m.role === "user" ? "ai-chat-msg--user" : ""}`}>
                  <div className={`ai-chat-bubble ${m.role === "user" ? "ai-chat-bubble--user" : "ai-chat-bubble--assistant"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {isSending ? <div className="ai-chat-status">{streamStatus || t("chat.thinking")}</div> : null}
              {error ? (
                <div className="alert alert-danger mt-2 mb-0">
                  <div className="fw-semibold">{t("chat.error_title")}</div>
                  <div className="small" style={{ whiteSpace: "pre-wrap" }}>
                    {error}
                  </div>
                  <div className="small mt-2">{t("chat.error_tip")}</div>
                </div>
              ) : null}
            </div>

            <div className="ai-chat-drawer__footer">
              <div className="ai-chat-input-row">
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder={t("chat.input_placeholder")}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={isSending}
                />
                <button className="btn btn-primary ai-chat-send-btn" onClick={handleSend} disabled={!canSend}>
                  {t("chat.send")}
                </button>
              </div>
              <div className="text-muted small mt-2">{t("chat.hint_enter")}</div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

